import React, { PropsWithChildren } from 'react';
import { motion } from 'framer-motion';

type RevealProps = PropsWithChildren<{
  className?: string;
  /** Seconds */
  delay?: number;
  /** Seconds */
  duration?: number;
  /** Initial translateY in px */
  offsetY?: number;
  /** Reveal once when entering viewport */
  once?: boolean;
  /** Portion of element that must be visible to trigger (0-1) */
  threshold?: number;
}>;

/**
 * Scroll-triggered fade-and-rise reveal wrapper using Framer Motion.
 * Applies a gentle, modern easing to match a "ふわっと" appearance.
 */
const Reveal: React.FC<RevealProps> = ({
  children,
  className,
  delay = 0,
  duration = 1.0,
  offsetY = 28,
  once = true,
  threshold = 0.08,
}) => {
  const GLOBAL_ADDITIONAL_DELAY_SECONDS = 0.2; // add slight global delay to smooth initial sequence
  const effectiveDelay = delay + GLOBAL_ADDITIONAL_DELAY_SECONDS;
  // Ensure content is visible immediately on environments lacking IntersectionObserver
  const canUseIntersectionObserver =
    typeof window !== 'undefined' && typeof (window as any).IntersectionObserver !== 'undefined';
  return (
    <motion.div
      className={className}
      initial={canUseIntersectionObserver ? 'hidden' : 'visible'}
      animate={canUseIntersectionObserver ? undefined : 'visible'}
      whileInView={canUseIntersectionObserver ? 'visible' : undefined}
      viewport={canUseIntersectionObserver ? { once, amount: threshold } : undefined}
      variants={{
        hidden: { opacity: 0, y: offsetY },
        visible: {
          opacity: 1,
          y: 0,
          transition: {
            duration,
            delay: effectiveDelay,
            ease: [0.22, 1, 0.36, 1],
          },
        },
      }}
    >
      {children}
    </motion.div>
  );
};

export default Reveal;

